import { Injectable, OnDestroy } from '@angular/core';
import {
    FlyFreelyLoggingService,
    PersonService,
    PersonWithRoles,
    PersonnelCriteria,
    PersonnelFieldSort,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Injectable()
export class PersonnelWidgetDataService implements OnDestroy {
    private personnelSource = new ReplaySubject<PersonWithRoles[]>(1);
    private workingSource = new BehaviorSubject<boolean>(false);
    private totalItemsSource = new ReplaySubject<number>(1);
    private currentPageSource = new BehaviorSubject<number>(0);

    working$ = this.workingSource.asObservable();
    personnel$ = this.personnelSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private personService: PersonService,
        private logging: FlyFreelyLoggingService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.personnelSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findPersonnel(
        organisationId: number,
        page: number,
        limit: number,
        criteria?: Partial<PersonnelCriteria>,
        sortFields?: PersonnelFieldSort[]
    ) {
        this.personService
            .findPersonnelGraphQL(
                organisationId,
                page,
                limit,
                criteria,
                sortFields
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.totalItemsSource.next(result.count);
                    this.currentPageSource.next(page);
                    this.personnelSource.next(result.results);
                },
                error: error => {
                    this.logging.error(
                        error,
                        `Error while loading personnel: ${error.message}`
                    );
                    this.personnelSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                }
            })
            .add(this.workTracker.createTracker());
    }
}
